import "./ButtonWithIcon.scss";
import cn from "classnames";
import React from "react";
import { ColorTheme } from "/Models/Models/ColorTheme";
import { IconProps } from "/Components/Icons/IconProps";

export interface ButtonWithIconProps {
  icon?: React.FC<IconProps>;
  iconPlacement?: "before" | "after";
  hoverIcon?: React.FC<IconProps>;
  hoverIconColor?: ColorTheme;
  colorTheme?: ColorTheme;
  text?: string;
  style?: "thin";
  onClickHandler?: () => void;
  ariaExpanded?: boolean;
  ariaControls?: string;
  lightIconOnDesktop?: boolean;
  hideTextOnMobile?: boolean;
  children?: React.ReactNode;
  href?: string;
  target?: "_blank" | "_self";
  rel?: string;
}

const ButtonWithIcon = ({
  text,
  colorTheme = ColorTheme.default,
  style,
  icon,
  iconPlacement,
  onClickHandler,
  ariaExpanded,
  ariaControls,
  lightIconOnDesktop,
  hideTextOnMobile,
  href,
  target = "_blank",
  rel = "noopener noreferrer",
}: ButtonWithIconProps) => {
  const IconComp = icon as React.FC<IconProps>;

  if (!onClickHandler && !href) {
    return null;
  }

  return (
    <ButtonWithIconWrapper
      className={cn("button-with-icon-norad", {
        "button-with-icon-norad--theme-default":
          colorTheme === ColorTheme.default,
        "button-with-icon-norad--theme-green": colorTheme === ColorTheme.green,
        "button-with-icon-norad--theme-dark-green":
          colorTheme === ColorTheme.iconStrong,
        "button-with-icon-norad--theme-black": colorTheme === ColorTheme.black,
        "button-with-icon-norad--style-thin": style === "thin",
        "button-with-icon-norad--no-text-on-mobile": hideTextOnMobile,
      })}
      href={href}
      onClickHandler={onClickHandler}
      ariaExpanded={ariaExpanded}
      ariaControls={ariaControls}
      target={target}
      rel={rel}
    >
      {IconComp && iconPlacement === "before" && (
        <>
          <span className="button-with-icon-norad__icon button-with-icon-norad__icon--mobile">
            <IconComp size={"20"} thickness={"Light"} color={colorTheme} />
          </span>
          <span className="button-with-icon-norad__icon button-with-icon-norad__icon--desktop">
            <IconComp
              size={"20"}
              thickness={lightIconOnDesktop ? "Light" : "Thin"}
              color={colorTheme}
            />
          </span>
        </>
      )}
      <span className="button-with-icon-norad__text">{text}</span>
      {IconComp && iconPlacement === "after" && (
        <>
          <span className="button-with-icon-norad__icon button-with-icon-norad__icon--mobile">
            <IconComp size={"20"} thickness={"Light"} color={colorTheme} />
          </span>
          <span className="button-with-icon-norad__icon button-with-icon-norad__icon--desktop">
            <IconComp
              size={"20"}
              thickness={lightIconOnDesktop ? "Light" : "Thin"}
              color={colorTheme}
            />
          </span>
        </>
      )}
    </ButtonWithIconWrapper>
  );
};

export default ButtonWithIcon;

interface ButtonWithIconWrapperProps extends ButtonWithIconProps {
  children: React.ReactNode;
  className?: string;
}

const ButtonWithIconWrapper = ({
  children,
  href,
  onClickHandler,
  ariaExpanded,
  ariaControls,
  className,
  target,
  rel,
}: ButtonWithIconWrapperProps) => {
  if (href) {
    return (
      <a href={href} className={className} target={target} rel={rel}>
        {children}
      </a>
    );
  }
  return (
    <button
      className={className}
      onClick={onClickHandler}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      type="button"
    >
      {children}
    </button>
  );
};
